export type IntelType =
  | "State guide"
  | "International guide"
  | "System guide"
  | "Playbook"
  | "Issue brief"
  | "Research";

export type IntelFact = { label: string; value: string };

export type IntelEntry = {
  id: string;
  type: IntelType;
  title: string;
  jurisdiction?: string;
  updated: string;
  summary: string;
  keywords: string[];
  facts: IntelFact[];
  related: string[];
};

export const searchSuggestions: string[] = [
  "California final pay",
  "Payroll requirements in Massachusetts",
  "Paycom effective dates",
  "Opening payroll operations in Canada",
];

export const intelEntries: IntelEntry[] = [
  {
    id: "ca-final-pay",
    type: "State guide",
    title: "California — Final pay",
    jurisdiction: "California",
    updated: "2026-10-14",
    summary: "When final wages are due at separation, what must be included, and the penalties for paying late.",
    keywords: ["final pay", "final paycheck", "termination", "resignation", "waiting time", "separation", "pto", "ca"],
    facts: [
      { label: "Involuntary termination", value: "All final wages due immediately at separation" },
      { label: "Resignation, 72+ hours’ notice", value: "Due on the last day of work" },
      { label: "Resignation, no notice", value: "Due within 72 hours" },
      { label: "Accrued vacation / PTO", value: "Paid out as wages at separation" },
      { label: "Late payment", value: "Waiting-time penalties of up to 30 days’ wages" },
    ],
    related: ["tx-final-pay", "ma-payroll", "playbook-911"],
  },
  {
    id: "ma-payroll",
    type: "State guide",
    title: "Massachusetts — Payroll requirements",
    jurisdiction: "Massachusetts",
    updated: "2026-09-22",
    summary: "Pay frequency, final pay, and paid family and medical leave obligations for Massachusetts employers.",
    keywords: ["requirements", "pay frequency", "final pay", "pfml", "paid family medical leave", "ma"],
    facts: [
      { label: "Pay frequency", value: "Weekly or bi-weekly for most hourly employees" },
      { label: "Final pay, involuntary", value: "Due on the day of discharge" },
      { label: "Final pay, resignation", value: "Due by the next regular payday" },
      { label: "Paid Family & Medical Leave", value: "Contributions remitted quarterly" },
      { label: "Registration", value: "Withholding account with the Department of Revenue" },
    ],
    related: ["ca-final-pay", "issue-multistate"],
  },
  {
    id: "paycom-effective",
    type: "System guide",
    title: "Paycom — Effective dates and retro changes",
    updated: "2026-09-30",
    summary:
      "How future-dated and retroactive changes should flow from employee records to payroll — and the checks that keep them from being missed.",
    keywords: ["paycom", "effective dates", "effective-dated", "retro", "retroactive", "future-dated", "rate change"],
    facts: [
      { label: "Covers", value: "Future-dated and retroactive job, rate, and deduction changes" },
      { label: "Check before cutoff", value: "Changes effective inside the open pay period" },
      { label: "Common failure", value: "Retro pay calculated by hand and never reconciled" },
      { label: "Owners", value: "Payroll lead and system administrator" },
    ],
    related: ["issue-regular-rate", "playbook-conversion"],
  },
  {
    id: "canada-opening",
    type: "International guide",
    title: "Canada — Opening payroll operations",
    jurisdiction: "Canada",
    updated: "2026-10-07",
    summary: "What an employer needs in place before running its first Canadian payroll.",
    keywords: ["canada", "opening", "open", "expansion", "cra", "cpp", "ei", "quebec", "international", "operations"],
    facts: [
      { label: "Registration", value: "CRA payroll program account (RP) under a business number" },
      { label: "Withholdings", value: "Income tax, CPP or QPP, and EI premiums" },
      { label: "Québec", value: "Separate provincial withholding and QPP through Revenu Québec" },
      { label: "Year-end", value: "T4 slips filed and issued by the last day of February" },
    ],
    related: ["uk-payroll", "playbook-conversion"],
  },
  {
    id: "ny-frequency",
    type: "State guide",
    title: "New York — Pay frequency",
    jurisdiction: "New York",
    updated: "2026-08-18",
    summary: "How often different categories of New York employees must be paid.",
    keywords: ["pay frequency", "manual workers", "weekly", "ny", "requirements"],
    facts: [
      { label: "Manual workers", value: "Weekly, unless the employer is authorized otherwise" },
      { label: "Clerical and other workers", value: "At least twice a month" },
      { label: "Commissioned salespeople", value: "At least once a month" },
    ],
    related: ["ma-payroll", "issue-multistate"],
  },
  {
    id: "tx-final-pay",
    type: "State guide",
    title: "Texas — Final pay",
    jurisdiction: "Texas",
    updated: "2026-07-29",
    summary: "Final wage deadlines for Texas employers.",
    keywords: ["final pay", "termination", "resignation", "tx", "separation"],
    facts: [
      { label: "Involuntary separation", value: "Due within six calendar days" },
      { label: "Resignation", value: "Due by the next regular payday" },
    ],
    related: ["ca-final-pay"],
  },
  {
    id: "uk-payroll",
    type: "International guide",
    title: "United Kingdom — Running payroll",
    jurisdiction: "United Kingdom",
    updated: "2026-09-09",
    summary: "Registration, reporting, and pension duties for employers paying people in the UK.",
    keywords: ["uk", "britain", "hmrc", "paye", "rti", "pension", "international", "opening", "operations"],
    facts: [
      { label: "Registration", value: "PAYE scheme with HMRC" },
      { label: "Reporting", value: "Full Payment Submission on or before each payday" },
      { label: "Pensions", value: "Automatic enrolment for eligible workers" },
    ],
    related: ["canada-opening"],
  },
  {
    id: "playbook-911",
    type: "Playbook",
    title: "Payroll without its owner: the first 72 hours",
    updated: "2026-10-02",
    summary: "What we do in the first three days after a payroll lead leaves without a handover.",
    keywords: ["payroll 911", "emergency", "departure", "quit", "left", "rescue", "coverage", "continuity"],
    facts: [
      { label: "Hours 0–8", value: "Secure access, the calendar, and the next pay date" },
      { label: "Hours 8–24", value: "Rebuild the cycle from the last two registers" },
      { label: "Hours 24–72", value: "Run, review, and release — then write down what we found" },
    ],
    related: ["ca-final-pay", "issue-regular-rate"],
  },
  {
    id: "playbook-conversion",
    type: "Playbook",
    title: "Vendor conversion: year-to-date balances",
    updated: "2026-08-26",
    summary: "Moving year-to-date earnings, taxes, and deductions between payroll vendors so year-end still ties out.",
    keywords: ["conversion", "vendor change", "migration", "ytd", "year-to-date", "switching", "implementation"],
    facts: [
      { label: "Best timing", value: "A quarter start — ideally January 1" },
      { label: "Must tie out", value: "YTD wages, taxes, and deductions, by employee" },
      { label: "Before cutover", value: "Two parallel payrolls that match" },
    ],
    related: ["paycom-effective", "canada-opening"],
  },
  {
    id: "issue-regular-rate",
    type: "Issue brief",
    title: "Regular rate: when differentials miss overtime",
    updated: "2026-09-16",
    summary:
      "Shift differentials and most bonuses belong in the overtime regular rate. When pay codes are set up wrong, overtime is underpaid every cycle.",
    keywords: ["overtime", "regular rate", "differential", "shift", "bonus", "flsa", "configuration", "earnings"],
    facts: [
      { label: "Rule", value: "Most differentials and nondiscretionary bonuses count toward the regular rate" },
      { label: "Where it breaks", value: "Earning codes set up outside the overtime calculation" },
      { label: "What we check", value: "Every earning code’s overtime treatment, against policy" },
    ],
    related: ["paycom-effective", "issue-multistate"],
  },
  {
    id: "issue-multistate",
    type: "Issue brief",
    title: "Remote employees and multi-state withholding",
    updated: "2026-08-04",
    summary: "Remote and hybrid work quietly creates new withholding, unemployment, and registration obligations.",
    keywords: ["multi-state", "remote", "withholding", "registration", "tax", "work location"],
    facts: [
      { label: "Trigger", value: "An employee working from a new state" },
      { label: "Often missed", value: "State registrations and unemployment accounts" },
      { label: "What we check", value: "Work locations in the HRIS against tax setup in payroll" },
    ],
    related: ["ma-payroll", "ny-frequency"],
  },
  {
    id: "research-18-months",
    type: "Research",
    title: "HRIS implementations, eighteen months after go-live",
    updated: "2026-10-21",
    summary: "A chapter preview from The State of Payroll Operations, 2026: what still isn’t working a year and a half later.",
    keywords: ["hris", "implementation", "research", "go-live", "report", "configuration"],
    facts: [
      { label: "Publication", value: "The State of Payroll Operations, 2026" },
      { label: "Chapter", value: "Two" },
      { label: "Status", value: "Publishing January 2027" },
    ],
    related: ["issue-regular-rate", "playbook-conversion"],
  },
];

export type Jurisdiction = {
  code: string;
  name: string;
  guides: number;
  updated: string;
  region: "us" | "intl";
};

export const jurisdictions: Jurisdiction[] = [
  { code: "CA", name: "California", guides: 14, updated: "2026-10-14", region: "us" },
  { code: "NY", name: "New York", guides: 12, updated: "2026-08-18", region: "us" },
  { code: "MA", name: "Massachusetts", guides: 11, updated: "2026-09-22", region: "us" },
  { code: "WA", name: "Washington", guides: 10, updated: "2026-10-21", region: "us" },
  { code: "IL", name: "Illinois", guides: 9, updated: "2026-09-02", region: "us" },
  { code: "NJ", name: "New Jersey", guides: 9, updated: "2026-08-12", region: "us" },
  { code: "CO", name: "Colorado", guides: 9, updated: "2026-09-10", region: "us" },
  { code: "TX", name: "Texas", guides: 8, updated: "2026-07-29", region: "us" },
  { code: "PA", name: "Pennsylvania", guides: 8, updated: "2026-07-15", region: "us" },
  { code: "OR", name: "Oregon", guides: 8, updated: "2026-08-28", region: "us" },
  { code: "GA", name: "Georgia", guides: 6, updated: "2026-06-30", region: "us" },
  { code: "FL", name: "Florida", guides: 5, updated: "2026-06-18", region: "us" },
  { code: "CAN", name: "Canada", guides: 7, updated: "2026-10-07", region: "intl" },
  { code: "GBR", name: "United Kingdom", guides: 6, updated: "2026-09-09", region: "intl" },
  { code: "IRL", name: "Ireland", guides: 4, updated: "2026-07-21", region: "intl" },
  { code: "MEX", name: "Mexico", guides: 4, updated: "2026-06-11", region: "intl" },
  { code: "DEU", name: "Germany", guides: 3, updated: "2026-05-27", region: "intl" },
  { code: "IND", name: "India", guides: 3, updated: "2026-05-14", region: "intl" },
];

export type LibraryColumn = { title: string; count: number; items: string[] };

export const libraryColumns: LibraryColumn[] = [
  {
    title: "System & vendor guides",
    count: 38,
    items: [
      "Paycom — Effective dates and retro changes",
      "Workday — HCM-to-payroll integration checkpoints",
      "UKG Pro — Time-to-pay rule mapping",
      "ADP Workforce Now — Earnings and deduction setup",
      "Dayforce — Pay policy configuration review",
    ],
  },
  {
    title: "Operational playbooks",
    count: 24,
    items: [
      "Payroll without its owner: the first 72 hours",
      "Vendor conversion: year-to-date balances",
      "Parallel payroll: what has to tie",
      "Year-end readiness, week by week",
      "Opening payroll in a new state",
    ],
  },
  {
    title: "Issue intelligence",
    count: 61,
    items: [
      "Regular rate: when differentials miss overtime",
      "Remote employees and multi-state withholding",
      "Benefit deductions drifting after open enrollment",
      "Retro pay after late rate changes",
      "Agency notices: triage and response",
    ],
  },
  {
    title: "Research library",
    count: 17,
    items: [
      "HRIS implementations, eighteen months after go-live",
      "Who runs payroll — and what happens when they leave",
      "Manual work: where the hours actually go",
      "Checks that exist versus checks that happen",
    ],
  },
];

export type IntelUpdate = { date: string; subject: string; change: string; type: IntelType };

export const intelUpdates: IntelUpdate[] = [
  { date: "2026-10-21", subject: "Washington", change: "Paid Family & Medical Leave guide updated for the 2027 premium rate", type: "State guide" },
  { date: "2026-10-21", subject: "Research", change: "Chapter preview added: HRIS implementations, eighteen months after go-live", type: "Research" },
  { date: "2026-10-14", subject: "California", change: "Final pay guide reviewed; penalty examples expanded", type: "State guide" },
  { date: "2026-10-07", subject: "Canada", change: "Updated for 2027 CPP and EI parameters", type: "International guide" },
  { date: "2026-09-30", subject: "Paycom", change: "Effective-date guide revised with a retro pay checklist", type: "System guide" },
  { date: "2026-09-16", subject: "Overtime", change: "New brief: differentials and the regular rate", type: "Issue brief" },
];
